/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { FC, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Alert,
  Stack,
  Divider,
  Icon,
  MenuItem,
  FormControl,
  Select,
  useTheme,
  Button,
} from '@mui/material';
import {
  AccountCircleOutlined,
  AlternateEmailOutlined,
  CalendarTodayOutlined,
  KeyboardArrowDown,
  SubjectOutlined,
} from '@mui/icons-material';
import TestIds from '../../../testIds';
import { selectCurrentFarmerAccount } from '../../azure-b2c/storage';
import { PageContainer } from '../../../components/layout/PageContainer';
import { BackButton } from '../../../components/BackButton';
import Typography from '../../../components/Typography';
import { useActivities, useActivitiesLookups } from '../api/hooks/useActivities';
import {
  ActivitiesResponse,
  Activity,
  ActivityStatus,
  Lookup,
  ContactPerson,
} from '../api/types/types';
import StatusBadge from '../components/StatusBadge/StatusBadge';
import { getActivityTypeLabel } from '../utils/lookupUtils';
import { getActivityTypeIconById } from '../utils/getActivityIcon';
import { formatDate } from '../utils/dateUtils';
import { ACTIVITIES_ROUTE } from '../activities-routes';
import { useThemeBackgroundColor } from '../../../hooks/useThemeBackgroundColor';
import ClickableContactNumber from '../../../components/ClickableContactNumber';
import ActivityDetailsSkeleton from '../components/ActivityDetailsSkeleton/ActivityDetailsSkeleton';
import CancelActivityModal from '../components/CancelActivityModal/CancelActivityModal';
import EditActivityModal from '../components/EditActivityModal/EditActivityModal';
import CompleteActivityModal from '../components/CompleteActivityModal/CompleteActivityModal';

const ActivityDetails: FC = (): JSX.Element => {
  const [showCancelActivityModal, setShowCancelActivityModal] = useState(false);
  const [showEditActivityModal, setShowEditActivityModal] = useState(false);
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const account = useSelector(selectCurrentFarmerAccount);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const {
    data: activitiesData,
    isLoading: activitiesLoading,
    error: activitiesError,
  } = useActivities(account?.accountNumber ?? '');
  const {
    data: lookupsData,
    isLoading: lookupsLoading,
    error: lookupsError,
  } = useActivitiesLookups();

  useThemeBackgroundColor((theme) => theme.colors.shade0);

  const [activity, campaigns] = useMemo(() => {
    if (!activitiesData || !id) {
      return [];
    }
    const activities = (activitiesData as ActivitiesResponse)?.activities || [];
    return [activities.find((a: Activity) => a.activityId === id), activitiesData.campaigns || []];
  }, [activitiesData, id]);

  const lookups = useMemo(() => {
    return (lookupsData as { lookups: Lookup[] })?.lookups || [];
  }, [lookupsData]);

  const contactPersons = useMemo(() => {
    return (activitiesData as ActivitiesResponse)?.contactPersons || [];
  }, [activitiesData]);

  const displayContact = useMemo(() => {
    if (activity?.contactId) {
      const contactPerson = contactPersons.find(
        (cp: ContactPerson) => cp.contactId === activity.contactId,
      );

      if (contactPerson) {
        return {
          name: `${contactPerson.firstName} ${contactPerson.lastName}`.trim(),
          email: contactPerson.email || 'Email not specified',
          phone: contactPerson.phoneNumber?.toString() || 'Phone not specified',
        };
      }
    }

    return {
      name: account?.contact?.name || 'Name not specified',
      email: account?.contact?.email || 'Email not specified',
      phone: account?.contact?.phone || 'Phone not specified',
    };
  }, [activity, contactPersons, account]);

  const isLoading = activitiesLoading || lookupsLoading;
  const error = activitiesError || lookupsError;
  const isEditable =
    activity?.status === ActivityStatus.Open || activity?.status === ActivityStatus.Scheduled;

  const BackToActivitiesButton = (
    <BackButton
      sx={{ justifyContent: 'flex-start', width: 'fit-content', minWidth: 0 }}
      text='All Activities'
      onClick={() => navigate(ACTIVITIES_ROUTE)}
      data-testid={TestIds.components.backButton.activityDetails}
    />
  );

  if (error) {
    return (
      <PageContainer data-testid={TestIds.feat.activities.activityDetails.component}>
        {BackToActivitiesButton}
        <Alert severity='error' data-testid={TestIds.feat.activities.activityDetails.error}>
          Failed to load activity details. Please try again later.
        </Alert>
      </PageContainer>
    );
  }

  if (isLoading) {
    return (
      <PageContainer data-testid={TestIds.feat.activities.activityDetails.component}>
        <ActivityDetailsSkeleton
          headerContent={BackToActivitiesButton}
          contactBackgroundColor={theme.colors.primary50}
        />
      </PageContainer>
    );
  }

  if (!activity) {
    return (
      <PageContainer data-testid={TestIds.feat.activities.activityDetails.component}>
        {BackToActivitiesButton}
        <Alert severity='warning' data-testid={TestIds.feat.activities.activityDetails.notFound}>
          Activity not found.
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer data-testid={TestIds.feat.activities.activityDetails.component}>
      {BackToActivitiesButton}
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        gap={2}
        mb={2}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <Typography variant='h3' fontWeight={700} fontSize='2rem' color='primary600'>
          {activity.subject}
        </Typography>
        {isEditable && (
          <Button
            variant='contained'
            data-testid={TestIds.feat.activities.activityDetails.editButton}
            onClick={() => setShowEditActivityModal(true)}
            sx={{
              minWidth: { xs: '100%', sm: '8.8rem' },
            }}
          >
            Edit Activity
          </Button>
        )}
      </Stack>
      <Stack
        flex={2}
        gap={3}
        sx={{
          minWidth: { xs: '100%', md: 639 },
          maxWidth: { xs: '100%', md: 738 },
          width: { xs: '100%', md: 'auto' },
        }}
      >
        {activity.createdBy ? (
          <>
            <Typography variant='body1' color='shade100' fontSize='1.125rem'>
              {`Created by ${activity.createdBy}`}
            </Typography>
          </>
        ) : null}

        {/* Status */}
        <Stack gap={0.5}>
          <Typography
            marginBottom={0}
            variant='body1'
            fontWeight={700}
            color='shade100'
            fontSize='1rem'
          >
            Status
          </Typography>
          {activity.status === ActivityStatus.Open ||
          activity.status === ActivityStatus.Scheduled ? (
            <FormControl sx={{ width: 260 }}>
              <Select
                data-testid={TestIds.feat.activities.activityDetails.statusDropdown}
                value={ActivityStatus.Open}
                IconComponent={KeyboardArrowDown}
                sx={{ height: '3rem',
                    '.MuiSelect-icon': {
                      top: 'calc(50% - .5em) !important',
                    },
                  }}
                onChange={(e) => {
                  const newStatus = e.target.value;
                  if (Number(newStatus) === ActivityStatus.Canceled) {
                    setShowCancelActivityModal(true);
                  }
                  if (Number(newStatus) === ActivityStatus.Completed) {
                    setIsCompleteModalOpen(true);
                  }
                }}
              >
                {[ActivityStatus.Open, ActivityStatus.Completed, ActivityStatus.Canceled].map(
                  (status) => (
                    <MenuItem key={status} value={status}>
                      <StatusBadge status={status} />
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          ) : (
            <StatusBadge status={activity.status} />
          )}
        </Stack>

        <Divider />

        {[
          {
            label: 'Activity Type',
            icon: <Icon color='disabled'>{getActivityTypeIconById(activity.activityType)}</Icon>,
            value: getActivityTypeLabel(lookups, activity.activityType),
          },
          {
            label: 'Description',
            icon: <SubjectOutlined color='disabled' />,
            value: activity.description ?? 'No description provided',
          },
          {
            label: 'Scheduled Date',
            icon: <CalendarTodayOutlined color='disabled' />,
            value: formatDate(activity.scheduleDate),
          },
          {
            label: 'Owner',
            icon: <AccountCircleOutlined color='disabled' />,
            value: activity.owner,
          },
        ].map(({ label, icon, value }) => (
          <div key={label}>
            <Typography
              variant={'body2'}
              fontWeight={700}
              fontSize='1rem'
              color='shade100'
              marginBottom={0.5}
            >
              {label}
            </Typography>
            <Stack direction='row' gap={1} alignItems='center' marginBottom={2}>
              {icon}
              <Typography variant='body1' fontWeight={400} fontSize='1rem'>
                {value}
              </Typography>
            </Stack>
            <Divider />
          </div>
        ))}

        {/* Customer Contact */}
        <Stack gap={2}>
          <Typography variant='h5' fontWeight={700} color='shade100'>
            Customer Contact
          </Typography>
          <Stack
            direction='row'
            gap={2}
            alignItems='center'
            sx={{
              backgroundColor: theme.colors.primary50,
              borderRadius: 2,
              padding: 2,
              minHeight: '4rem',
            }}
          >
            <Stack>
              <Typography
                variant='body1'
                fontWeight={700}
                fontSize='1rem'
                color='primary500'
                marginBottom={2}
              >
                {displayContact.name}
              </Typography>
              {displayContact.email && (
                <Stack direction='row' alignItems='center' gap={2} marginBottom={1}>
                  <AlternateEmailOutlined color='disabled' fontSize='small' />
                  <Typography variant='body2' fontWeight={400} fontSize='1rem'>
                    {displayContact.email}
                  </Typography>
                </Stack>
              )}
              {displayContact.phone && <ClickableContactNumber phone={displayContact.phone} />}
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      {showCancelActivityModal && (
        <CancelActivityModal
          open={showCancelActivityModal}
          activity={activity}
          onClose={() => setShowCancelActivityModal(false)}
        />
      )}

      {showEditActivityModal && (
        <EditActivityModal
          open={showEditActivityModal}
          activity={activity}
          onClose={() => setShowEditActivityModal(false)}
          campaigns={campaigns}
          lookups={lookups}
          contactPersons={(activitiesData as ActivitiesResponse)?.contactPersons || []}
        />
      )}
      <CompleteActivityModal
        open={isCompleteModalOpen}
        onClose={() => setIsCompleteModalOpen(false)}
        activity={activity}
        accountNumber={account?.accountNumber || ''}
        campaigns={campaigns}
        activityName={activity.subject || ''}
        lookups={lookups}
      />
    </PageContainer>
  );
};

export default ActivityDetails;
